import { ListItem, ListItemText, ListItemSecondaryAction, IconButton, Typography } from "@material-ui/core";
import History from "@material-ui/icons/HistoryOutlined";
import Videocam from "@material-ui/icons/VideocamOutlined";
import clsx from "clsx";

import { useGetVmsSearchRightsQuery } from "@/api/elasticSearch/vmsSearchApi";
import * as FullStory from "@/utilities/hooks/fullstory";
import { handleHighlight } from "@/utilities/jsx";
import { isUnitActive } from "@/utilities/liveUnitHelper";
import { SearchResult, LiveUnitSearchData, SearchIndex } from "@/utilities/types/search";

import { resultStyles } from "./ResultStyles";


interface LiveUnitProps {
    result: SearchResult<LiveUnitSearchData>;
    historyUnitId: number | null;
    onSelect: ((result: SearchResult<LiveUnitSearchData>) => void) | null;
    onStreamClick: ((result: SearchResult<LiveUnitSearchData>) => void) | null;
    onHistoryClick: (() => void) | null;
}


const LiveUnitResult = (props: LiveUnitProps) => {
    const { result, historyUnitId, onSelect, onStreamClick, onHistoryClick } = props;
    const { data: searchRightsData } = useGetVmsSearchRightsQuery({ indices: [SearchIndex.LiveUnits, SearchIndex.ArchiveCabinets, SearchIndex.Clients] });

    const unit = result.source;

    const classes = resultStyles();
    const unitName = result.highlight?.["name"] ?? unit.name;
    const clientName = result.highlight?.["client_name"] ?? unit.client_name;
    const locationName = result.highlight?.["location_name"] ?? unit.location_name;
    const serial = result.highlight?.["serial"] ?? unit.serial;

    // Check if live unit is deactivated
    const isUnitAcive = isUnitActive(result.source.date_activated, result.source.date_deactivated);

    const address = () => {
        if (unit.location_street ||
            unit.location_city ||
            unit.location_state ||
            unit.location_zip) {

            const street = result.highlight?.["location_street"] ?? unit.location_street;
            const city = result.highlight?.["location_city"] ?? unit.location_city;
            const state = result.highlight?.["location_state"] ?? unit.location_state;
            const zip = result.highlight?.["location_zip"] ?? unit.location_zip;

            return (<>
                {
                    unit.location_street?.trim() && (<>
                        {handleHighlight(street)}
                        <span>, </span>
                    </>)
                }
                {
                    unit.location_city?.trim() && (<>
                        {handleHighlight(city)}
                        <span>, </span>
                    </>)
                }
                {handleHighlight(state)}
                <span> </span>
                {handleHighlight(zip)}

            </>);
        } else {
            return <span>No address saved for this Live Unit</span>;
        }
    };

    return (
        <ListItem
            data-testid={"live-unit-result"}
            className={clsx(classes.item, FullStory.names.classes.searchResults.viewUnit)}
            onClick={onSelect == null ? undefined : () => onSelect(result)}
            alignItems="flex-start"
            dense
            button
        >
            <ListItemText
                primary={
                    <Typography className={classes.title} variant="body1" component={"div"}>
                        {handleHighlight(locationName)}
                    </Typography>
                }
                disableTypography
                secondary={
                    <div className={classes.subheader}>
                        <Typography noWrap variant="body2" component={"div"}>
                            {address()}
                        </Typography>
                        <Typography noWrap variant="body2" component={"div"}>
                            {searchRightsData?.data.is_multi_client &&
                                <>
                                    {handleHighlight(clientName)}
                                    <span> | </span>
                                </>
                            }
                            {handleHighlight(unitName)}
                            {serial && !unitName.toLowerCase().includes(serial.toLowerCase()) &&
                                <>
                                    <span>, </span>
                                    {handleHighlight(serial)}
                                </>
                            }
                        </Typography>
                    </div>
                }
            />
            <ListItemSecondaryAction>
                {isUnitAcive && <IconButton
                    aria-label="navigate-stream"
                    className={FullStory.names.classes.searchResults.viewStream}
                    onClick={onStreamClick == null ? undefined : () => onStreamClick(result)}
                    disabled={onStreamClick == null}
                >
                    <Videocam />
                </IconButton>}
                <IconButton
                    aria-label="navigate-history"
                    className={FullStory.names.classes.searchResults.viewHistory}
                    onClick={onHistoryClick == null ? undefined : () => onHistoryClick()}
                    disabled={historyUnitId == null}
                >
                    <History />
                </IconButton>
            </ListItemSecondaryAction>
        </ListItem>
    );
};

export default LiveUnitResult;
